import { useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface Plan {
  total: number;
  assignable: number;
  groups: Array<{
    driver: { id: string; name: string };
    zones: Array<{ code: string; name: string }>;
    orderIds: string[];
    count: number;
  }>;
  skipped: Array<{
    reason: string;
    label: string;
    hint: string;
    zone: { code: string; name: string } | null;
    orderIds: string[];
    count: number;
  }>;
  truncated: boolean;
}

interface Result {
  summary: { total: number; succeeded: number; failed: number };
  results: Array<{ orderId: string; status: string; error?: { message: string } }>;
}

/**
 * 按区域默认司机批量派单。
 *
 * ★ 一次一百单派下去,点之前必须看清三件事:
 *   哪些单派给谁、哪些派不出去、派不出去我该干什么。
 *
 *   所以先预览再确认,而且**派不出去的那部分要和能派的一样显眼** ——
 *   把它折叠起来的话,调度员会以为「全派完了」,
 *   剩下那几单要等到客户打电话才被发现。
 */
export function AutoAssignDialog({
  orderIds,
  onClose,
  onDone,
}: {
  orderIds: string[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const p = await api<Plan>('/orders/auto-assign/preview', {
          method: 'POST',
          body: { orderIds },
        });
        if (alive) setPlan(p);
      } catch (e) {
        if (alive) setError(e instanceof ApiFailure ? e.message : '算不出派单方案');
      }
    })();
    return () => {
      alive = false;
    };
  }, [orderIds]);

  async function confirm() {
    if (!plan || busy) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api<Result>('/orders/auto-assign', {
          method: 'POST',
          body: {
            orderIds,
            // 重传时逐单幂等 —— 网络抖一下不该把同一批派两遍
            batchKey: `auto-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`,
          },
        }),
      );
      await onDone();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '派单失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="按区域派单"
        className="w-full max-w-2xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">按区域派单</h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          每单派给它所在区域的默认司机。先看一眼,确认无误再派。
        </p>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {!plan && !error && <p className="text-sm text-slate-500">正在算…</p>}

        {/* ── 派完了 ── */}
        {result && (
          <div className="rounded-xl border border-green-300 dark:border-green-900
                          bg-green-50 dark:bg-green-950/40 p-4">
            <div className="font-semibold text-green-800 dark:text-green-300">派单完成</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              成功 {result.summary.succeeded} 单
              {result.summary.failed > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  {' '}
                  · 失败 {result.summary.failed} 单
                </span>
              )}
            </div>
            {/* 失败的要说清哪一单、为什么 —— 只给个数字等于让人自己去翻 */}
            {result.results
              .filter((r) => r.status !== 'SUCCESS')
              .map((r) => (
                <div key={r.orderId} className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {r.orderId}:{r.error?.message ?? '派单失败'}
                </div>
              ))}
            <button
              onClick={onClose}
              className="mt-3 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
            >
              完成
            </button>
          </div>
        )}

        {/* ── 预览 ── */}
        {plan && !result && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap text-sm">
              <span className="text-slate-900 dark:text-slate-100">选中 {plan.total} 单</span>
              <span className="text-green-700 dark:text-green-400">可派 {plan.assignable}</span>
              {plan.total - plan.assignable > 0 && (
                <span className="text-amber-700 dark:text-amber-400">
                  派不出 {plan.total - plan.assignable}
                </span>
              )}
            </div>

            {plan.groups.length > 0 && (
              <section>
                <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                  派给谁
                </h4>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y
                                divide-slate-100 dark:divide-slate-700/50">
                  {plan.groups.map((g) => (
                    <div
                      key={g.driver.id}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span className="text-slate-900 dark:text-slate-100">{g.driver.name}</span>
                      <span className="text-slate-500">
                        {/* 凭哪个区派的要写出来 —— 派错了才知道从哪查 */}
                        {g.zones.map((z) => z.code).join('、')} · {g.count} 单
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ★ 派不出去的和能派的一样显眼。折叠起来等于没说 */}
            {plan.skipped.length > 0 && (
              <section>
                <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                  这些派不出去
                </h4>
                <div className="rounded-xl border border-amber-200 dark:border-amber-900
                                bg-amber-50/60 dark:bg-amber-950/20 divide-y
                                divide-amber-200/60 dark:divide-amber-900/50">
                  {plan.skipped.map((s) => (
                    <div key={`${s.reason}:${s.zone?.code ?? ''}`} className="px-3 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-900 dark:text-slate-100">
                          {s.label}
                          {s.zone && (
                            <span className="text-slate-500"> · {s.zone.code} {s.zone.name}</span>
                          )}
                        </span>
                        <span className="text-slate-500">{s.count} 单</span>
                      </div>
                      {/* 每条都要能回答「那我该怎么办」 */}
                      <div className="mt-0.5 text-xs text-slate-500">{s.hint}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {plan.truncated && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                单太多,这次只处理前 200 单。派完再来一次。
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                           text-slate-700 dark:text-slate-300"
              >
                取消
              </button>
              <button
                onClick={() => void confirm()}
                disabled={busy || plan.assignable === 0}
                className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium
                           disabled:opacity-40"
              >
                {busy ? '派单中…' : plan.assignable === 0 ? '没有能派的单' : `派 ${plan.assignable} 单`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
